import { fabric } from 'fabric';
import { getCompGeometry } from './geometry';

export const COMP_SETTINGS_KEY = 'comp';
export const COMP_BACKGROUND_ID = 'comp_background';

let showComps = false;

export function getComp(preview) {
  const value = preview?.data?.[COMP_SETTINGS_KEY];
  let comp = value;
  if (typeof value === 'string') {
    try {
      comp = JSON.parse(value || 'null');
    } catch (_) {
      return null;
    }
  }
  return getCompGeometry(preview?.dimension?.[0], preview?.dimension?.[1], comp) ? comp : null;
}

export function getCompGroup(preview) {
  const groupId = getComp(preview)?.groupId;
  return groupId && preview.canvas.getObjects().find(object => object.id === groupId);
}

export function getCompObjects(preview) {
  const comp = getComp(preview);
  if (!comp) return [];

  if (Array.isArray(comp.fieldIds)) {
    const memberIds = new Set(comp.fieldIds);
    return preview.canvas.getObjects().filter(object =>
      memberIds.has(object.id) || memberIds.has(object.id?.replace(/_/g, ''))
    );
  }
  if (!comp.groupId) return [];

  const group = getCompGroup(preview);
  if (group) return group.getObjects();

  let groups = preview.data?.group_fields || [];
  if (typeof groups === 'string') {
    try {
      groups = JSON.parse(groups);
    } catch (_) {
      return [];
    }
  }
  const groupData = Array.isArray(groups) && groups.find(item => item.id === comp.groupId);
  const memberIds = new Set((groupData?.fields || []).map(field =>
    typeof field === 'string' ? field : field?.id
  ));
  return preview.canvas.getObjects().filter(object =>
    memberIds.has(object.id) || memberIds.has(object.id?.replace(/_/g, ''))
  );
}

export function setShowComps(value) {
  showComps = !!value;
}

export function isShowComps() {
  return showComps;
}

function ensureOutline(canvas, className) {
  let outline = canvas.wrapperEl?.querySelector(`.${className}`);
  if (!outline && canvas.wrapperEl) {
    outline = document.createElement('div');
    outline.className = className;
    Object.assign(outline.style, {
      position: 'absolute',
      zIndex: '4',
      boxSizing: 'border-box',
      pointerEvents: 'none'
    });
    canvas.wrapperEl.appendChild(outline);
  }
  return outline;
}

export function setCompSelection(preview, region) {
  const canvas = preview?.canvas;
  if (!canvas) return;

  canvas._compSelectedRegion = region;
  const compOutline = canvas.wrapperEl?.querySelector('.comp-outline');
  const layoutOutline = canvas.wrapperEl?.querySelector('.comp-layout-outline');
  if (compOutline) {
    compOutline.style.border = region === 'comp' ? '1px solid #209c86' : '1px solid #d2d6dc';
    compOutline.hidden = !showComps;
  }
  if (layoutOutline) layoutOutline.hidden = !showComps || region !== 'layout';
}

function getCompLabel(canvas) {
  return canvas.wrapperEl?.closest('.grid-stack-item')?.querySelector('.comp-dimensions')
    || canvas.wrapperEl?.querySelector('.comp-dimensions');
}

export function resetCompCanvas(preview) {
  const canvas = preview?.canvas;
  const state = canvas?._compCanvasState;
  if (!state) return;

  canvas.discardActiveObject();
  canvas.setDimensions({ width: state.width, height: state.height });
  canvas.setDimensions({ width: state.cssWidth, height: state.cssHeight }, { cssOnly: true });
  canvas.setViewportTransform(state.viewportTransform);
  if (state.item) {
    state.item.style.minWidth = state.minWidth;
    state.item.style.minHeight = state.minHeight;
    state.item.classList.remove('has-visible-comp');
  }
  const label = getCompLabel(canvas);
  if (label) label.hidden = true;
  const outline = canvas.wrapperEl?.querySelector('.comp-outline');
  if (outline) outline.hidden = true;
  const layoutOutline = canvas.wrapperEl?.querySelector('.comp-layout-outline');
  if (layoutOutline) layoutOutline.hidden = true;
  delete canvas._compCanvasState;
  canvas.calcOffset();
}

export function syncCompCanvas(preview) {
  const canvas = preview?.canvas;
  if (!canvas) return;

  resetCompCanvas(preview);
  const comp = getComp(preview);
  let background = canvas.getObjects().find(object => object.id === COMP_BACKGROUND_ID);
  if (!comp) {
    if (background) canvas.remove(background);
    const label = getCompLabel(canvas);
    if (label) label.remove();
    const outline = canvas.wrapperEl?.querySelector('.comp-outline');
    if (outline) outline.remove();
    const layoutOutline = canvas.wrapperEl?.querySelector('.comp-layout-outline');
    if (layoutOutline) layoutOutline.remove();
    delete canvas._compSelectedRegion;
    return;
  }

  const geometry = getCompGeometry(preview.dimension[0], preview.dimension[1], comp);
  const group = getCompGroup(preview);
  if (group?.getObjects().length) {
    group.toActiveSelection();
    canvas.discardActiveObject();
  } else if (group) {
    canvas.remove(group);
  }
  if (!background) {
    background = new fabric.Rect({
      id: COMP_BACKGROUND_ID,
      fill: '#fff',
      selectable: false,
      evented: false
    });
    canvas.insertAt(background, 0, false);
  }
  background.set({ ...geometry.worldComp, stroke: null, strokeWidth: 0, visible: showComps });
  background.setCoords();
  canvas.moveTo(background, 0);
  getCompObjects(preview).forEach(object => { object.visible = showComps; });

  let label = getCompLabel(canvas);
  if (label?.tagName !== 'SPAN') {
    label?.remove();
    label = null;
  }
  const labelContainer = canvas.wrapperEl?.closest('.grid-stack-item') || canvas.wrapperEl;
  if (!label && labelContainer) {
    label = document.createElement('span');
    label.className = 'comp-dimensions';
    Object.assign(label.style, {
      position: 'absolute',
      zIndex: '5',
      padding: '2px 6px',
      background: 'rgba(255, 255, 255, 0.9)',
      fontSize: '12px',
      border: '0',
      pointerEvents: 'none'
    });
    labelContainer.appendChild(label);
  }
  if (label) {
    if (label.parentElement !== labelContainer) labelContainer.appendChild(label);
    label.textContent = `Comp ${comp.width} × ${comp.height} px`;
    label.hidden = !showComps;
    label.style.left = labelContainer === canvas.wrapperEl
      ? '8px'
      : `${(parseFloat(getComputedStyle(labelContainer).paddingLeft) || 0) + 8}px`;
    label.style.top = labelContainer === canvas.wrapperEl
      ? '-24px'
      : `${(parseFloat(getComputedStyle(labelContainer).paddingTop) || 0) - 24}px`;
  }

  const outline = ensureOutline(canvas, 'comp-outline');
  const layoutOutline = ensureOutline(canvas, 'comp-layout-outline');
  if (layoutOutline) layoutOutline.style.border = '1px solid #209c86';
  setCompSelection(preview, canvas._compSelectedRegion);

  if (!showComps) {
    canvas.requestRenderAll();
    return;
  }

  const base = {
    width: canvas.width,
    height: canvas.height,
    cssWidth: parseFloat(canvas.lowerCanvasEl.style.width) || canvas.width,
    cssHeight: parseFloat(canvas.lowerCanvasEl.style.height) || canvas.height,
    viewportTransform: [...canvas.viewportTransform],
    item: canvas.wrapperEl?.closest('.grid-stack-item'),
  };
  base.minWidth = base.item?.style.minWidth || '';
  base.minHeight = base.item?.style.minHeight || '';
  canvas._compCanvasState = base;
  const widthRatio = geometry.width / preview.dimension[0];
  const heightRatio = geometry.height / preview.dimension[1];
  canvas.setDimensions({ width: base.width * widthRatio, height: base.height * heightRatio });
  canvas.setDimensions({ width: base.cssWidth * widthRatio, height: base.cssHeight * heightRatio }, { cssOnly: true });
  if (base.item) {
    base.item.style.minWidth = `${base.cssWidth * widthRatio}px`;
    base.item.style.minHeight = `${base.cssHeight * heightRatio}px`;
    base.item.classList.add('has-visible-comp');
  }
  if (outline) {
    Object.assign(outline.style, {
      left: '0px',
      top: `${comp.side === 'below' ? base.cssHeight : 0}px`,
      width: `${base.cssWidth * comp.width / preview.dimension[0]}px`,
      height: `${base.cssHeight * comp.height / preview.dimension[1]}px`
    });
  }
  if (layoutOutline) {
    Object.assign(layoutOutline.style, {
      left: `${base.cssWidth * geometry.layout.left / preview.dimension[0]}px`,
      top: `${base.cssHeight * geometry.layout.top / preview.dimension[1]}px`,
      width: `${base.cssWidth}px`,
      height: `${base.cssHeight}px`
    });
  }
  const viewportTransform = [...base.viewportTransform];
  // Shift the view for a left comp; the saved artwork coordinates stay in the original layout space.
  viewportTransform[4] += geometry.layout.left * viewportTransform[0];
  viewportTransform[5] += geometry.layout.top * viewportTransform[3];
  canvas.setViewportTransform(viewportTransform);
  canvas.calcOffset();
  canvas.requestRenderAll();
}
